/**
 * BrandLogo — single source of truth for the Carp Cast brand mark.
 *
 * Variants:
 *   - "full":  icon + "THE CARP CAST" wordmark (default)
 *   - "icon":  icon-only — used in tight spaces (mobile navbar, favicons)
 *   - "splash": full mark rendered large with gold glow (used in
 *               loading/auth screens)
 *
 * Sizes are TAILWIND HEIGHT CLASSES so we get crisp scaling on retina
 * (the underlying PNG is rendered at high DPI).
 *
 * All variants are wrapped in a Link by default (defaults to /dashboard).
 * Pass `as="div"` for a plain non-linked render (auth pages, footers).
 */
import { Link } from 'react-router-dom';

const SIZE_FULL = {
  xs: 'h-8',         // 32px — footer
  sm: 'h-10',        // 40px
  md: 'h-12',        // 48px — mobile navbar
  lg: 'h-14',        // 56px — desktop navbar (+27% over old h-11)
  xl: 'h-20 sm:h-24', // 80–96px — auth pages
  splash: 'h-28 sm:h-36', // 112–144px — loading/splash
};

const SIZE_ICON = {
  xs: 'h-7',
  sm: 'h-9',
  md: 'h-11',
  lg: 'h-12',
  xl: 'h-16',
};

const BrandLogo = ({
  variant = 'full',
  size = 'lg',
  as: As = 'a',  // 'a' = react-router Link wrapper, else 'div'/'span'
  to = '/dashboard',
  className = '',
  glow = false,
  withTransition = true,
  testId = 'brand-logo',
  onClick,
}) => {
  const isIcon = variant === 'icon';
  const isSplash = variant === 'splash';
  const src = isIcon ? '/carpcast-icon.png' : '/carpcast-logo.png';
  const sizeMap = isIcon ? SIZE_ICON : SIZE_FULL;
  const effectiveSize = isSplash ? SIZE_FULL.splash : sizeMap[size] || sizeMap.lg;

  const img = (
    <img
      src={src}
      alt="The Carp Cast"
      draggable={false}
      className={[
        effectiveSize,
        'block w-auto max-w-none select-none object-contain',
        withTransition && 'transition-all duration-300 ease-out',
        // Hover/active states only on linked instances
        As === 'a' && 'group-hover:scale-[1.04] group-hover:drop-shadow-[0_0_18px_rgba(200,178,115,0.35)]',
        glow && 'drop-shadow-[0_0_24px_rgba(200,178,115,0.28)]',
        isSplash && 'animate-pulse-soft drop-shadow-[0_0_28px_rgba(200,178,115,0.3)]',
      ].filter(Boolean).join(' ')}
    />
  );

  // Non-linked render (auth pages can pass as="div")
  if (As !== 'a') {
    return (
      <As className={`inline-flex items-center shrink-0 ${className}`} data-testid={testId}>
        {img}
      </As>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testId}
      aria-label="The Carp Cast — Home"
      className={`group inline-flex items-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c8b273]/60 rounded ${className}`}
    >
      {img}
    </Link>
  );
};

export default BrandLogo;
